import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { Field, Modal } from './ui';

const FALLBACK = {
  subject: 'Your quote {{quote.number}} from Simply Totes',
  body: `Hi {{contact.first_name}},

Your quote for {{quote.reference}} is ready to view here:

{{link}}

You can accept it online, or reply with any questions or changes.

Kind regards,
{{user.name}}
Simply Totes + Co`,
};

/** Fill {{placeholders}} in an email template. */
export function fillTemplate(t: string, vars: Record<string, string | number | null | undefined>) {
  return t.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => String(vars[k] ?? '').trim());
}

export default function SendQuote({ quote, onClose, onSent }: {
  quote: { id: string; number: number; reference: string; public_token: string; contact_id: string | null; customer_name?: string };
  onClose: () => void;
  onSent: () => void;
}) {
  const { profile } = useAuth();
  const link = `${window.location.origin}/q/${quote.public_token}`;
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const [tpl, contact] = await Promise.all([
        supabase.from('email_templates').select('subject, body').eq('type', 'quote').eq('archived', false)
          .order('is_default', { ascending: false }).limit(1).maybeSingle(),
        quote.contact_id
          ? supabase.from('contacts').select('first_name, last_name, email').eq('id', quote.contact_id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      const t = (tpl.data as { subject: string; body: string } | null) ?? FALLBACK;
      const c = contact.data as { first_name: string | null; last_name: string | null; email: string | null } | null;
      const vars = {
        'quote.number': quote.number, 'quote.reference': quote.reference || 'your order', link,
        'contact.first_name': c?.first_name || 'there', 'contact.name': [c?.first_name, c?.last_name].filter(Boolean).join(' '),
        'customer.name': quote.customer_name, 'user.name': profile?.full_name,
      };
      setTo(c?.email ?? '');
      setSubject(fillTemplate(t.subject, vars));
      setBody(fillTemplate(t.body, vars));
    })();
  }, [quote.id]);

  const markSent = async () => {
    await supabase.rpc('mark_quote_sent', { p_quote: quote.id, p_to: to });
    onSent();
  };

  const openEmail = async () => {
    const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    await markSent();
  };

  const copy = async () => {
    await navigator.clipboard.writeText(`${subject}\n\n${body}`);
    setCopied(true);
  };

  return (
    <Modal title={`Send quote ${quote.number}`} onClose={onClose}
      footer={<>
        <span className="muted small grow">Opens your email app with this message ready to send. The quote is marked Sent.</span>
        <button onClick={copy}>{copied ? 'Copied' : 'Copy text'}</button>
        <button onClick={async () => { await markSent(); onClose(); }}>Mark as sent</button>
        <button className="primary" onClick={openEmail} disabled={!to}>Open in email</button>
      </>}>
      <div className="stack" style={{ maxWidth: 720 }}>
        <Field label="To"><input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" /></Field>
        <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label="Message"><textarea value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 260 }} /></Field>
        <div className="small muted">Customer link: <a href={`${link}?preview=1`} target="_blank" rel="noreferrer">{link}</a></div>
      </div>
    </Modal>
  );
}
